import { apiGetPublicBundles } from '@/api/functions'
import { ApiBundlePreset, WorkstationDetails } from '@/api/types/config'
import { StorageTierCodename } from '@/api/types/user'
import { Codename } from '@/api/types/workstation'
import { fitsWithin } from '@/services/numbers'
import { isNumberString } from '@/services/strings'
import {
  BundlePreset,
  BundleSummary,
  FeatureListingElement,
  WorkstationType,
} from '@/store/types/config'
import { keys2Camel } from './objects'
import {
  daysToSeconds,
  milisecondsToSeconds,
  nowMilisecondsTimestamp,
  secondsToDays,
  secondsToMiliseconds,
} from './time'

type PossibleBundleSelectionContext = 'onboarding' | 'change'

export type BundleOfferInfo = Pick<
  BundlePreset,
  'name' | 'priceUsdPerMonth' | 'features' | 'trialInfo' | 'workstationDetails' | 'year' | 'month'
>

const DRIVE_DATA_DELETION_AFTER_TRIAL_END_DAYS = 20

/**
 * The action written on the button on BundleCards is loosely based
 * on a bundle type, yet not included on the model. This replaces a
 * one-time computed property
 * @takes a trial info
 * @returns a button text
 */
export function getBundleButtonText(
  bundle: BundlePreset,
  context: PossibleBundleSelectionContext = 'change',
): string {
  if (context === 'onboarding') {
    const { priceUsdPerMonth } = bundle
    return (
      // trialInfo
      // ? `Start ${trialInfo.days}-day trial`
      // :
      isNumberString(priceUsdPerMonth) ? 'Purchase' : "Let's chat"
    )
  }
  return bundle.emailOnly ? 'Contact us' : 'Pick'
}

/**
 * To display a nicely formatted features string on Account Overview
 * @note the benefits that you GET, not "($19 per month graphic) +1 computer" ones.
 * Luckily, the "additional" offer ones all start with a plus sign 🤞‍‍🍀
 *
 * @takes bundle's features
 * @returns a nicely formatted string
 */
export function getFeaturesString(features: FeatureListingElement[]): string {
  if (!features) return ''
  const filteredFeatureStringsArray = features
    .map((feature: FeatureListingElement) => feature.text)
    .filter((featureText: string) => !featureText.startsWith('+')) // this is a nasty, but quick way to drop the features like "($19 per month graphic) +1 computer"
  return filteredFeatureStringsArray.join(', ')
}

/**
 * Used by account overview
 * @param bundles
 * @param codename sought for
 * @returns a limited BundlePreset object
 */
export function getBundleOfferDataByCodename(
  bundles: BundlePreset[],
  codename: string,
): BundleOfferInfo | undefined {
  if (!bundles || !codename) return
  const found = bundles.find((bundle) => bundle.codename === codename)
  if (found) {
    const { name, features, priceUsdPerMonth, workstationDetails, year, month } = found
    return { name, features, priceUsdPerMonth, workstationDetails, year, month }
  }
  return
}

/**
 * Used by /workstations view to determine 'Add' button behavior on /workstations
 *
 * This is extremaly crude. I am sorry 🙏‍‍
 * The data this should probably be returned by backend.
 */
export function isStudio(bundles: BundlePreset[], codename: string): boolean {
  const planDetails = getBundleOfferDataByCodename(bundles, codename)
  if (!planDetails) return false
  const extendablePlans = ['studio', 'enterprise']
  return extendablePlans.includes(planDetails.name.toLowerCase())
}

export function getUserBundleWorkstations(
  bundles: BundlePreset[],
  userBundleCodename: string,
): WorkstationDetails | undefined {
  const bundle = bundles?.find((bundle: BundlePreset) => bundle.codename === userBundleCodename)
  return bundle?.workstationDetails
}

export function isStillWithinStudioLimit(number: number) {
  // this is an ugly way to hardcode the Studio Plan's 10 workstation limit
  return fitsWithin(number, 10)
}

/**
 * Get price for a new workstation. This will probably be depending on the bundle.
 */
export function getAdditionalWorkstationMonthlyPrice(
  _bundles: BundlePreset[],
  _codename: string,
): string {
  return '79.00'
}

export function getWorkstationPowerFromCodename(codename: Codename): string {
  return codename.split('-')[0]
}

/**
 * Get proper price of a given workstation considering available bundles
 *
 * @param bundles available bundles to choose price from
 * @param storageTierCodename currently chosen bundle
 * @param workstation current workstation details
 * @param workstationPrice fallback price
 * @param stillHasHoursRemaining ensure the user is entitled to the bundle prices
 * @returns a price
 */
export function workstationPriceAwareOfBundlesAndHourLimit(
  bundles: BundlePreset[],
  storageTierCodename: StorageTierCodename,
  workstation: WorkstationType,
  workstationPrice: string,
  stillHasHoursRemaining: boolean,
): string {
  let workstationPower = ''
  // get the power category
  if (workstation.codename) {
    workstationPower = getWorkstationPowerFromCodename(workstation.codename)
  }
  // seek the said power category among available bundles
  const workstationPowerPrice = getUserBundleWorkstations(bundles, storageTierCodename)?.[
    workstationPower as keyof WorkstationDetails
  ]
  // if it is found and the user still has hours, return bundle ws price
  if (workstationPowerPrice?.price && stillHasHoursRemaining) {
    return workstationPowerPrice.price
    // otherwise, return the price imprinted on the workstation document
  } else if (workstation) {
    return workstationPrice
  }
  return '???'
}

export const bundleSummary: BundleSummary = {
  freelancer: { icon: 'cloud storage free.png', alt: 'computer, hard drive and pre-paid model' },
  pro: { icon: 'cloud storage pro.png', alt: 'compuer, hard drive and pre-paid model' },
  studio: { icon: 'studioSummary.svg', alt: 'two of five computers' },
  enterprise: { icon: 'enterpriseSummary.svg', alt: 'eleven computers' },
}

export function trialPeriodExpiryDate(trialEndSeconds: number): string {
  const date = new Date(secondsToMiliseconds(trialEndSeconds))
  if (date <= new Date()) {
    // expired
    return ''
  }
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  const dateStr = date.toLocaleDateString('en-US', options)
  const minutes = date.getMinutes()
  const timeStr = `${date.getHours()}:${minutes < 10 ? '0' + minutes : minutes}`
  return `${dateStr} ${timeStr}`
}

export async function getSelectableBundles(): Promise<BundlePreset[]> {
  const allBundles: ApiBundlePreset[] = await apiGetPublicBundles()
  const translatedAllBundles: BundlePreset[] = keys2Camel(allBundles)
  return translatedAllBundles.filter((bundle) => bundle.selectable)
}

/**
 * Helper function for TrialBanner
 * @param trialEndSecondsTimestamp
 * @param creditsLeft
 * @returns a banner content string or nothing, in case not applicable
 */
export function getTrialBannerStatus(
  trialEndSecondsTimestamp: number,
  creditsLeft: number,
): string | null {
  // if there was no trial to speak of or there's a trail ongoing with some credits, return early
  if (!trialEndSecondsTimestamp) return null
  const nowSeconds = milisecondsToSeconds(nowMilisecondsTimestamp())
  if (nowSeconds <= trialEndSecondsTimestamp && creditsLeft > 0) return null

  // ...otherwise, determine the cause for trail end and return appropriate message.
  const reason = creditsLeft <= 0 ? 'You have no credits left.' : 'Your trial period has eneded.'
  const driveDataDeletionTimestampSeconds =
    trialEndSecondsTimestamp + daysToSeconds(DRIVE_DATA_DELETION_AFTER_TRIAL_END_DAYS)
  const deltaToDeletionDays = secondsToDays(nowSeconds - driveDataDeletionTimestampSeconds)

  // if delta is negative, it means that there's still time
  const daysLeftNotice =
    deltaToDeletionDays <= 0
      ? ` in ${Math.abs(deltaToDeletionDays)} day${deltaToDeletionDays < -1 ? 's' : ''}`
      : ''
  return `${reason}  Subscribe to a plan to continue using your Renderro workstation. Your files are about to be deleted${daysLeftNotice}.`
}


// codenames of plans custom-2022-06-us 9 custom-2022-06-eu to show migrated users CustomPlanBanner
export const customBundlesCodenames = ["custom-2022-06-us", "custom-2022-06-eu"]